/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom, Subject} from 'rxjs';
import {Component, Inject, OnInit, signal} from '@angular/core';
import {completeCopyModal} from '../common/util/complete-copy-modal';
import {BsModalRef} from 'ngx-bootstrap/modal';
import * as _ from 'lodash';

import template from './line-item-template-copy-modal.component.html';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';
import {Uuid} from '../../components/util/uuid';
import {lineItemModDates, setLineItemDefaults} from './line-item-commons';
import {LineItemTemplateApiService} from './line-item-template-api.service';
import {AdnModalLoadingService} from '../common/util/adn-modal-loading.service';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';

export interface LineItemTemplateCopyResult {
  newId: string;
  destination: boolean;
  copyToLineItem: boolean;
}

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED],
  selector: 'adn-line-item-template-copy-modal',
  template
})
export class LineItemTemplateCopyModalComponent implements OnInit {
  model: any;
  copyToLineItem = false;
  fromLineItemList = false;

  readonly onCopied = new Subject<LineItemTemplateCopyResult>();

  readonly lineItem = signal<any>({});
  lineItemTemplate: any;
  readonly allTemplates = signal<any[]>([]);
  readonly templatesResolved = signal(false);
  isMarketplace = false;
  isNew = true;
  readonly updating = signal(false);
  copy = {targeting: true, creatives: true};
  destination = true;
  readonly disabled = signal(false);
  readonly validationErrors = signal<any[]>([]);

  constructor(
    public bsModalRef: BsModalRef,
    @Inject(LineItemTemplateApiService) private readonly LineItemTemplate: LineItemTemplateApiService,
    @Inject(AdnModalLoadingService) private readonly adnModalLoading: any,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
  ) {
    this.isMarketplace = LocalNetworkProfile.isMarketplacePlatform();
  }

  ngOnInit(): void {
    if (this.fromLineItemList) {
      // Search does not index line item templates; load via the resource API.
      firstValueFrom(this.LineItemTemplate.listOptions()).then((data: any) => {
        this.allTemplates.set(data.results || []);
        this.templatesResolved.set(true);
      });
    } else {
      this.assignInfo(this.model);
    }
  }

  templateChanged(): void {
    const templateId = _.get(this.lineItemTemplate, 'id');
    if (!templateId) {
      return;
    }
    // List results can be incomplete; fetch the full template before copy fields.
    this.updating.set(true);
    firstValueFrom(this.LineItemTemplate.get(templateId)).then((data: any) => {
      this.lineItemTemplate = data;
      this.assignInfo(data);
      this.updating.set(false);
    }, () => {
      this.assignInfo(this.lineItemTemplate);
      this.updating.set(false);
    });
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }

  copyCampaign(): void {
    const lineItem = this.lineItem();
    if (!lineItem) {
      return;
    }
    this.disabled.set(true);
    this.validationErrors.set([]);
    this.adnModalLoading.applyModalLoading();
    const newId = Uuid.generate();
    lineItemModDates(this.adnTimeShifter, lineItem, lineItem.modStartDate, lineItem.modEndDate);
    firstValueFrom(this.LineItemTemplate.copyTemplate(lineItem, newId, this.copy.targeting, this.copyToLineItem)).then(() => {
      this.disabled.set(false);
      this.adnModalLoading.removeModalLoading();
      completeCopyModal(this.bsModalRef, this.onCopied, {
        destination: this.destination,
        newId,
        copyToLineItem: this.copyToLineItem
      });
    }, (errors: any) => {
      this.disabled.set(false);
      this.validationErrors.set(_.get(errors, ['data', 'errors'], []) || []);
      this.adnModalLoading.removeModalLoading();
    });
  }

  private assignInfo(data: any): void {
    if (!data) {
      return;
    }
    const lineItem = _.cloneDeep(data);
    lineItem.name = data.name + ' - derived from line item template';
    setLineItemDefaults(this.LocalNetworkProfile, this, lineItem);
    if (lineItem.startDate) {
      lineItem.modStartDate = lineItem.startDate;
    }
    if (lineItem.endDate) {
      lineItem.modEndDate = lineItem.endDate;
    }
    this.lineItem.set(lineItem);
  }
}
